import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { getAuthInstance } from '../firebase';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import UserProfileDropdown from './UserProfileDropdown';
import ChatBot from './ChatBot';
import styles from './Layout.module.css';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const isBrokerOnly = userRole === 'broker_owner';
  const hideSidebar = location.pathname === '/app/cma' || location.pathname === '/app/admin' ||
    (location.pathname === '/app/broker' && isBrokerOnly);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const authInstance = await getAuthInstance();
        if (!authInstance) {
          setLoading(false);
          return;
        }

        const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
          if (user) {
            try {
              await user.getIdToken(true);
              const tokenResult = await user.getIdTokenResult();
              setUserRole(tokenResult.claims.role);
            } catch (error) {
              console.error('Error getting user role:', error);
            }
          }
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error checking auth:', error);
        setLoading(false);
      }
    };

    checkUserRole();
  }, []);

  const contentAreaClass = hideSidebar || userRole === 'admin_user' || isBrokerOnly
    ? `${styles.contentArea} ${styles.contentAreaFullWidth}`
    : styles.contentArea;

  return (
    <div className={styles.appLayout}>
      {/* Header - Consistent with landing page */}
      <header className={styles.appHeader}>
        <div className={styles.headerContent}>
          <div className={styles.logoSection}>
            <div className={styles.logo} onClick={() => navigate('/')}>
              <img
                src="/vates-domus-ai-removebg-preview.png?v=1"
                alt="Vates Domus AI"
                className={styles.logoImage}
              />
            </div>
            <div className={styles.poweredBy}>
              <span className={styles.poweredByText}>Powered by</span>
              <img
                src="/partner-logo.svg"
                alt="Realty.com"
                className={styles.partnerLogo}
              />
            </div>
          </div>

          <nav className={styles.appNav}>
            <div className={`${styles.navLinks} ${styles.desktopNav}`}>
              <span
                onClick={() => window.open('/faq', '_blank')}
                title="Frequently Asked Questions"
                className={styles.faqLink}
              >
                FAQ
              </span>
              <span className={styles.navLabel}>Welcome</span>
            </div>

            {/* Mobile Hamburger Menu - Toggles sidebar on mobile */}
            <div
              className={styles.mobileMenuToggle}
              onClick={() => {
                if (window.innerWidth <= 768) {
                  setIsSidebarOpen(!isSidebarOpen);
                } else {
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                }
              }}
              aria-label="Toggle menu"
            >
              <div className={`${styles.hamburger} ${isMobileMenuOpen || isSidebarOpen ? styles.hamburgerActive : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>

            <div className={styles.userActions}>
              <UserProfileDropdown />
            </div>
          </nav>
          
          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <div className={styles.mobileMenuOverlay} onClick={() => setIsMobileMenuOpen(false)}>
              <div className={styles.mobileMenuContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.mobileNavLinks}>
                  <button
                    onClick={() => {
                      window.open('/faq', '_blank');
                      setIsMobileMenuOpen(false);
                    }}
                    title="Frequently Asked Questions"
                    className={styles.mobileFaqBtn}
                  >
                    FAQ
                  </button>
                  <span className={styles.navLabel}>Welcome</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {!hideSidebar && userRole !== 'admin_user' && !isBrokerOnly && (
          <Sidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
        )}
        <div className={contentAreaClass}>
          {children}
        </div>
      </div>
      
      {/* Bottom Navigation - Mobile Only */}
      {!hideSidebar && userRole !== 'admin_user' && !isBrokerOnly && <BottomNav />}
      
      {/* ChatBot - appears on all protected app pages except admin */}
      {userRole !== 'admin_user' && !isBrokerOnly && <ChatBot />}
    </div>
  );
};

export default Layout; 